import { useCallback, useEffect, useRef, useState } from "react";
import type {
  CreateSlideConfig,
  GenerationPhase,
  GenerationRun,
  SlideGenerator,
  SlidePlan,
} from "./types";

interface GenState {
  phase: GenerationPhase;
  plans: SlidePlan[];
  current: number;
  total: number;
  error: string | null;
}

const INITIAL: GenState = { phase: "idle", plans: [], current: 0, total: 0, error: null };

/**
 * Owns generation state for the Create-slides flow and adapts the
 * SlideGenerator callbacks into React state. Pass the real backend generator;
 * if it's null (not signed in / backend not configured), `start` surfaces a
 * clear error instead of running.
 */
export function useCreateSlideGenerator(generator: SlideGenerator | null) {
  const runRef = useRef<GenerationRun | null>(null);
  const [state, setState] = useState<GenState>(INITIAL);

  // Cancel any in-flight generation when the consuming component unmounts (e.g.
  // the user leaves the Create-slides tab mid-stream) — otherwise the async
  // stream keeps running, setting state on an unmounted component and inserting
  // slides into the deck after the UI is gone.
  useEffect(() => () => runRef.current?.cancel(), []);

  const updateSlide = useCallback(
    (index: number, status: SlidePlan["status"], detail: SlidePlan["detail"]) =>
      setState((s) => ({
        ...s,
        plans: s.plans.map((p) => (p.index === index ? { ...p, status, detail } : p)),
      })),
    [],
  );
  const start = useCallback(
    (config: CreateSlideConfig) => {
      if (!generator) {
        setState({
          ...INITIAL,
          phase: "error",
          error: "Slide generation isn't available — sign in and ensure the backend is configured.",
        });
        return;
      }
      setState({ ...INITIAL, phase: "outlining" });
      runRef.current = generator.generate(config, {
        onPlan: (plans) => setState((s) => ({ ...s, plans, total: plans.length })),
         onSlideUpdate: updateSlide,
        onProgress: (current, total) => setState((s) => ({ ...s, current, total })),
        onPhase: (phase: GenerationPhase) => setState((s) => ({ ...s, phase })),
        onError: (message) => setState((s) => ({ ...s, error: message })),
      });
    },
    [generator, updateSlide],
  );

  const cancel = useCallback(() => runRef.current?.cancel(), []);
  const reset = useCallback(() => {
    runRef.current?.cancel();
    setState(INITIAL);
  }, []);

  return { ...state, start, cancel, reset };
}
